import type { WorkspaceEntity } from "./types";
import { classifyActivity, mapActivityIdentityKey } from "./activity-insights";
import { localeTag } from "@/i18n";

export type ActivityGroup = {
  key: string;
  label: string;
  compactLabel: string;
};

function startOfDay(value: Date) {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate());
}

function startOfWeek(value: Date) {
  const day = startOfDay(value);
  const weekday = day.getDay() || 7;
  day.setDate(day.getDate() - weekday + 1);
  return day;
}

function sameMonth(left: Date, right: Date) {
  return left.getFullYear() === right.getFullYear() && left.getMonth() === right.getMonth();
}

export function activityGroupFor(value: string, now = new Date()): ActivityGroup {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return { key: "unknown", label: "Ohne Zeitangabe", compactLabel: "?" };
  const today = startOfDay(now);
  const activityDay = startOfDay(date);
  const dayDifference = Math.round((today.getTime() - activityDay.getTime()) / 86_400_000);
  if (dayDifference === 0) return { key: "today", label: "Heute", compactLabel: "H" };
  if (dayDifference === 1) return { key: "yesterday", label: "Gestern", compactLabel: "G" };

  const thisWeek = startOfWeek(now);
  const activityWeek = startOfWeek(date);
  const weekDifference = Math.round((thisWeek.getTime() - activityWeek.getTime()) / (7 * 86_400_000));
  if (weekDifference === 0) return { key: "this-week", label: "Diese Woche", compactLabel: "W" };
  if (weekDifference === 1) return { key: "last-week", label: "Letzte Woche", compactLabel: "LW" };
  if (sameMonth(date, now)) return { key: "this-month", label: "Dieser Monat", compactLabel: "M" };

  const previousMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  if (sameMonth(date, previousMonth)) return { key: "last-month", label: "Letzter Monat", compactLabel: "LM" };
  return {
    key: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`,
    label: new Intl.DateTimeFormat(localeTag(), { month: "long", year: "numeric" }).format(date),
    compactLabel: new Intl.DateTimeFormat(localeTag(), { month: "short" }).format(date).slice(0, 3)
  };
}

export type ActivityListRow =
  | { type: "group"; key: string; group: ActivityGroup }
  | { type: "entity"; key: string; entity: WorkspaceEntity };

export function expandActivityOccurrences(entities: WorkspaceEntity[]) {
  return entities.flatMap((entity) => {
    if (!entity.visitOccurrences?.length) return [entity];
    const hasExplicitCurrentVisit = Boolean(
      entity.live
        && entity.currentVisitId
        && entity.visitOccurrences.some((occurrence) => occurrence.id === entity.currentVisitId)
    );
    const fallbackCurrentVisitId = entity.live && !hasExplicitCurrentVisit
      ? [...entity.visitOccurrences].sort((left, right) => right.lastSeenAt.localeCompare(left.lastSeenAt))[0]?.id
      : undefined;
    const orderedOccurrences = [...entity.visitOccurrences].sort((left, right) => {
      const leftCurrent = entity.live && left.id === (hasExplicitCurrentVisit ? entity.currentVisitId : fallbackCurrentVisitId);
      const rightCurrent = entity.live && right.id === (hasExplicitCurrentVisit ? entity.currentVisitId : fallbackCurrentVisitId);
      if (leftCurrent !== rightCurrent) return Number(rightCurrent) - Number(leftCurrent);
      return right.lastSeenAt.localeCompare(left.lastSeenAt);
    });
    return orderedOccurrences.map((occurrence, index) => {
      const journey = entity.journey.filter((event) => event.sessionId === occurrence.id);
      return {
        ...entity,
        id: `${entity.id}::visit:${occurrence.id}`,
        activitySourceEntityId: entity.id,
        activityOccurrenceId: occurrence.id,
        activitySessionNumber: occurrence.sessionNumber,
        activityReturning: occurrence.returning,
        activityIsLatest: index === 0,
        engagementMs: occurrence.engagementMs ?? journey.reduce((sum, event) => sum + Math.max(0, event.durationMs ?? 0), 0),
        engagementMeasurement: occurrence.engagementMeasurement ?? entity.engagementMeasurement,
        journey,
        firstSeenAt: occurrence.startedAt,
        lastSeenAt: occurrence.lastSeenAt,
        currentPath: occurrence.path,
        organizationId: occurrence.organizationId ?? entity.organizationId,
        companyName: occurrence.companyName ?? entity.companyName,
        domain: occurrence.domain ?? entity.domain,
        acquisition: occurrence.acquisition ?? entity.acquisition,
        acquisitionSource: occurrence.acquisitionSource ?? (
          occurrence.acquisition === undefined ? entity.acquisitionSource : undefined
        ),
        visitorLabel: occurrence.visitorLabel ?? entity.visitorLabel,
        requestTraceId: occurrence.requestTraceId ?? entity.requestTraceId,
        activityOriginLabel: occurrence.activityOriginLabel ?? entity.activityOriginLabel,
        activityOriginDetail: occurrence.activityOriginDetail ?? entity.activityOriginDetail,
        activityOriginLocation: occurrence.activityOriginLocation ?? entity.activityOriginLocation,
        live: entity.live && occurrence.id === (
          hasExplicitCurrentVisit ? entity.currentVisitId : fallbackCurrentVisitId
        )
      };
    });
  });
}

function duplicateActivityKey(entity: WorkspaceEntity) {
  const visitorIdentity = entity.visitorProfileId
    || entity.linkedVisitorProfileId
    || entity.visitorLabel?.trim().toLocaleLowerCase("de");
  if (!visitorIdentity) return "";
  return [
    mapActivityIdentityKey(entity),
    visitorIdentity,
    entity.firstSeenAt,
    entity.lastSeenAt,
    entity.currentPath || "",
    entity.consent.status
  ].join("|");
}

function activityRichness(entity: WorkspaceEntity) {
  return (entity.detailsLoaded === false ? 0 : 20)
    + (entity.consent.status === "accepted" ? 8 : 0)
    + (entity.location.precision !== "unknown" ? 4 : 0)
    + Math.min(entity.journey.length, 10)
    + Math.min(entity.replaySessions?.length ?? 0, 5);
}

/**
 * Removes only provably identical visitor rows. Different visitors at the same
 * company and time remain separate; this is intentionally stricter than the
 * continuous-visit folding used inside one profile.
 */
export function deduplicateExpandedActivities(entities: WorkspaceEntity[]) {
  const unique = new Map<string, WorkspaceEntity>();
  const unkeyed: WorkspaceEntity[] = [];
  entities.forEach((entity) => {
    const key = duplicateActivityKey(entity);
    if (!key) {
      unkeyed.push(entity);
      return;
    }
    const current = unique.get(key);
    if (!current || activityRichness(entity) > activityRichness(current)) unique.set(key, entity);
  });
  return [...unique.values(), ...unkeyed].sort((left, right) => right.lastSeenAt.localeCompare(left.lastSeenAt));
}

function aggregateIdentityKey(entity: WorkspaceEntity) {
  if (classifyActivity(entity) === "recognized") return mapActivityIdentityKey(entity);
  return `${classifyActivity(entity)}:${entity.visitorProfileId || entity.linkedVisitorProfileId || entity.activitySourceEntityId || entity.id}`;
}

function relatedVisitOwners(entities: WorkspaceEntity[]) {
  return entities.flatMap((entity) => [
    entity,
    entity.organizationAccount,
    ...(entity.organizationVisitors ?? []),
    ...(entity.organizationAccount?.organizationVisitors ?? [])
  ].filter((candidate): candidate is WorkspaceEntity => Boolean(candidate)));
}

/** Counts only visit groups backed by an occurrence, journey session or replay. */
export function concreteActivityVisitCount(entities: WorkspaceEntity[]) {
  const owners = relatedVisitOwners(entities);
  const ownerOccurrences = (owner: WorkspaceEntity) => owner.detailVisitOccurrences ?? owner.visitOccurrences ?? [];
  const visitIds = new Set<string>();

  owners.forEach((owner) => {
    const ownerKey = owner.visitorProfileId || owner.organizationId || owner.id;
    owner.journey.forEach((event) => {
      const sessionId = event.sessionId || `activity-${ownerKey}`;
      visitIds.add(sessionId);
    });
    ownerOccurrences(owner).forEach((occurrence) => {
      // A technical link to a later consented profile identifies the company,
      // but it does not erase the separately retained pre-consent visit window.
      // Identical IDs are still naturally deduplicated by the Set.
      visitIds.add(occurrence.id);
    });
    (owner.replaySessions ?? []).forEach((session) => visitIds.add(session.id));
  });

  return visitIds.size;
}

/**
 * A company-name/domain search becomes one company result while an unsearched
 * timeline and searches for visitor/page attributes keep their visit rows.
 */
function aggregateActivityResultsInternal(
  entities: WorkspaceEntity[],
  options: { visibleVisitCountOnly?: boolean; aggregateScopeKey?: string } = {}
) {
  const rows: WorkspaceEntity[] = [];
  const groupIndexes = new Map<string, number>();
  const groupMembers = new Map<string, WorkspaceEntity[]>();
  entities.forEach((entity) => {
    const key = aggregateIdentityKey(entity);
    const members = groupMembers.get(key) ?? [];
    members.push(entity);
    groupMembers.set(key, members);
    if (!groupIndexes.has(key)) {
      groupIndexes.set(key, rows.length);
      rows.push(entity);
    }
  });
  groupMembers.forEach((members, key) => {
    if (members.length < 2 && !options.visibleVisitCountOnly) return;
    const index = groupIndexes.get(key)!;
    const representative = [...members].sort((left, right) => {
      const detailDifference = Number(right.detailsLoaded !== false) - Number(left.detailsLoaded !== false);
      if (detailDifference) return detailDifference;
      return right.lastSeenAt.localeCompare(left.lastSeenAt);
    })[0];
    const sourceId = representative.activitySourceEntityId || representative.id;
    // Only count visit rows for which the workspace has concrete evidence.
    // Legacy organization counters may outlive their underlying signal data
    // and must not make the list disagree with the detail timeline.
    const exactCompanyVisitCount = Math.max(
      0,
      ...members
        .filter((member) => member.organizationVisitCountExact === true)
        .map((member) => member.organizationVisitCount ?? 0)
    );
    // A complete backend visit index is authoritative. During progressive
    // loading the workspace can briefly contain both sides of a newly linked
    // consent transition; using the raw member count in that state recreates
    // a phantom extra visit even though the exact company history is already
    // available (ATP exposed this as 38 list rows versus 37 real visits).
    const knownVisitCount = options.visibleVisitCountOnly
      ? members.length
      : exactCompanyVisitCount || Math.max(
          members.length,
          concreteActivityVisitCount(members)
        );
    rows[index] = {
      ...representative,
      id: `${sourceId}::activity-aggregate:${options.aggregateScopeKey
        ? `${encodeURIComponent(options.aggregateScopeKey)}:`
        : ""}${encodeURIComponent(key)}`,
      activitySourceEntityId: sourceId,
      activityOccurrenceId: undefined,
      activitySessionNumber: undefined,
      activityReturning: knownVisitCount > 1,
      activityIsLatest: true,
      activitySearchAggregate: true,
      activityAggregateScope: options.visibleVisitCountOnly ? "recency" : "search",
      activitySearchVisitCount: knownVisitCount
    };
  });
  return rows;
}

export function aggregateActivityResults(entities: WorkspaceEntity[]) {
  return aggregateActivityResultsInternal(entities);
}

/**
 * Summarizes repeated companies and visitors inside each visible recency
 * bucket. Counts deliberately describe only that bucket (for example,
 * "Heute"), not the company's all-time visit index.
 */
export function aggregateActivityResultsByRecency(entities: WorkspaceEntity[], now = new Date()) {
  const buckets = new Map<string, WorkspaceEntity[]>();
  entities.forEach((entity) => {
    const key = activityGroupFor(entity.lastSeenAt, now).key;
    const members = buckets.get(key) ?? [];
    members.push(entity);
    buckets.set(key, members);
  });
  return [...buckets.entries()].flatMap(([bucketKey, members]) =>
    aggregateActivityResultsInternal(members, {
      visibleVisitCountOnly: true,
      aggregateScopeKey: `recency:${bucketKey}`
    })
  );
}

export function aggregateCompanySearchResults(entities: WorkspaceEntity[], search: string) {
  return search.trim() ? aggregateActivityResults(entities) : entities;
}

export function buildActivityRows(entities: WorkspaceEntity[], grouped: boolean, now = new Date()): ActivityListRow[] {
  const keyCounts = new Map<string, number>();
  const uniqueKey = (base: string) => {
    const occurrence = keyCounts.get(base) ?? 0;
    keyCounts.set(base, occurrence + 1);
    return occurrence ? `${base}::row:${occurrence}` : base;
  };
  if (!grouped) return entities.map((entity) => ({ type: "entity", key: uniqueKey(entity.id), entity }));
  const rows: ActivityListRow[] = [];
  let previousGroupKey = "";
  for (const entity of entities) {
    const group = activityGroupFor(entity.lastSeenAt, now);
    if (group.key !== previousGroupKey) {
      rows.push({ type: "group", key: uniqueKey(`group-${group.key}`), group });
      previousGroupKey = group.key;
    }
    rows.push({ type: "entity", key: uniqueKey(entity.id), entity });
  }
  return rows;
}
